package features.members.handlers

import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLInputElement
import shared.utils.CSVUtils
import shared.ui.ModalManager
import AppState
import features.schedule.data.ScheduleRepository
import features.attendance.domain.AttendanceRecord
import features.overtime.domain.OvertimeRequest
import features.timeoff.domain.TimeOffRequest

// Import/Export Modal Handler Functions
fun handleFileImport(input: HTMLInputElement, format: String) {
    console.log("Importing file as $format")
    val file = input.asDynamic().files[0]
    if (file != null) {
        when (format) {
            "csv" -> {
                // Use existing CSV import logic
                val reader = js("new FileReader()")
                reader.onload = { event: dynamic ->
                    val csvContent = event.target.result as String
                    console.log("CSV content loaded")
                    // Process CSV (reuse existing logic from handleCSVFileSelect)
                    ModalManager.close()
                    ModalManager.show("import-members-modal")
                    window.setTimeout({
                        val fileInput = document.getElementById("csv-file-input") as? HTMLInputElement
                        fileInput?.addEventListener("change", { handleCSVFileSelect() })
                        // Trigger the file input with the selected file
                        val dataTransfer = js("new DataTransfer()")
                        dataTransfer.items.add(file)
                        fileInput?.asDynamic().files = dataTransfer.files
                        fileInput?.dispatchEvent(js("new Event('change')"))
                    }, 100)
                }
                reader.readAsText(file)
            }
            "excel" -> {
                console.log("📊 Reading Excel file...")
                val reader = js("new FileReader()")
                reader.onload = { event: dynamic ->
                    val data = event.target.result
                    
                    // Use SheetJS to parse Excel
                    val workbook = js("XLSX.read(data, { type: 'binary' })")
                    val firstSheetName = workbook.SheetNames[0]
                    val worksheet = workbook.Sheets[firstSheetName]
                    
                    // Convert to CSV format
                    val csvContent = js("XLSX.utils.sheet_to_csv(worksheet)") as String
                    console.log("✅ Excel converted to CSV, length:", csvContent.length)
                    
                    // Parse CSV using existing logic
                    val parseResult = CSVUtils.parseCSV(csvContent)
                    importPreviewData = parseResult
                    
                    console.log("✅ Valid members: ${parseResult.validMembers.size}")
                    console.log("❌ Errors: ${parseResult.errors.size}")
                    
                    // Close import/export modal and show import preview modal
                    ModalManager.close()
                    ModalManager.show("import-members-modal")
                    
                    // Update preview
                    window.setTimeout({
                        updateImportPreview(parseResult)
                    }, 100)
                }
                reader.readAsBinaryString(file)
            }
        }
    }
}

fun exportMembers(format: String) {
    val teamId = AppState.currentTeam?.id ?: return
    val team = AppState.currentTeam ?: return
    val members = AppState.members[teamId] ?: emptyList()
    
    if (members.isEmpty()) {
        window.alert("No members to export")
        return
    }
    
    console.log("========================================")
    console.log("📊 MEMBER EXPORT - $format")
    console.log("========================================")
    console.log("Members count: ${members.size}")
    members.forEach { member ->
        console.log("Member: ${member.name} (${member.email}) - Role: ${member.role}")
    }
    
    // Handle CSV export separately - show date picker modal
    if (format == "csv") {
        console.log("📄 Showing CSV export date picker...")
        showCSVExportDatePicker(members, team.name)
        return
    }
    
    // For Excel export, fetch all related data
    // Get current user info for role-based access
    val currentUserId = AppState.currentUser?.id ?: ""
    val userRole = features.attendance.helpers.AttendanceHelpers.getUserRole(currentUserId, teamId)
    
    // Fetch all data from Firebase for Excel
    features.attendance.data.AttendanceRepository.getAttendanceRecordsForUser(teamId, currentUserId, userRole)
        .then { attendanceRecords ->
            features.overtime.data.OvertimeRepository.getOvertimeRequests(teamId)
                .then { overtimeRequests ->
                    features.timeoff.data.TimeOffRepository.getTimeOffRequests(teamId)
                        .then { timeoffRequests ->
                            console.log("✅ All data fetched successfully")
                            console.log("  - Attendance: ${attendanceRecords.size}")
                            console.log("  - Overtime: ${overtimeRequests.size}")
                            console.log("  - Time Off: ${timeoffRequests.size}")
                            
                            // Use filter from AppState (Schedule Tab filter)
                            val filterMonth = AppState.scheduleMonth
                            val filterYear = AppState.scheduleYear
                            console.log("📅 Using filter: Month=$filterMonth, Year=$filterYear")
                            
                            // Filter data by selected month/year
                            val filteredAttendance = attendanceRecords.filter { record ->
                                val dateParts = record.date.split("-")
                                if (dateParts.size == 3) {
                                    val year = dateParts[0].toIntOrNull()
                                    val month = dateParts[1].toIntOrNull()
                                    year == filterYear && month == filterMonth
                                } else false
                            }
                            
                            val filteredOvertime = overtimeRequests.filter { request ->
                                val dateParts = request.date.split("-")
                                if (dateParts.size == 3) {
                                    val year = dateParts[0].toIntOrNull()
                                    val month = dateParts[1].toIntOrNull()
                                    year == filterYear && month == filterMonth
                                } else false
                            }
                            
                            val filteredTimeoff = timeoffRequests.filter { request ->
                                val dateParts = request.startDate.split("-")
                                if (dateParts.size == 3) {
                                    val year = dateParts[0].toIntOrNull()
                                    val month = dateParts[1].toIntOrNull()
                                    year == filterYear && month == filterMonth
                                } else false
                            }
                            
                            console.log("📊 Filtered data counts:")
                            console.log("  - Attendance: ${filteredAttendance.size} (from ${attendanceRecords.size})")
                            console.log("  - Overtime: ${filteredOvertime.size} (from ${overtimeRequests.size})")
                            console.log("  - Time Off: ${filteredTimeoff.size} (from ${timeoffRequests.size})")
                            
                            // Log sample data for debugging
                            if (filteredAttendance.isNotEmpty()) {
                                console.log("📋 Sample attendance record: ${filteredAttendance[0].userId} | ${filteredAttendance[0].date} | ${filteredAttendance[0].status}")
                            } else {
                                console.warn("⚠️ No attendance records for $filterMonth/$filterYear")
                            }
                            
                            // Fetch Schedule for Calendar
                            features.schedule.data.ScheduleRepository.loadSchedule(teamId, filterYear, filterMonth)
                                .then { schedule ->
                                    console.log("✅ Schedule loaded: ${if (schedule != null) "Yes" else "No"}")
                                    
                                    // Generate Excel file with filtered data
                                    val shifts = AppState.shiftTemplates[teamId] ?: emptyList()
                                    console.log("📋 Using ${shifts.size} shift templates from AppState")
                                    
                                    generateMemberExcel(
                                        members, 
                                        filteredAttendance, 
                                        filteredOvertime, 
                                        filteredTimeoff, 
                                        team.name,
                                        shifts,
                                        schedule?.assignments
                                    )
                                    
                                    ModalManager.close()
                                }
                                .catch { error ->
                                    console.error("❌ Failed to fetch schedule:", error)
                                    // Proceed without schedule
                                    val shifts = AppState.shiftTemplates[teamId] ?: emptyList()
                                    generateMemberExcel(
                                        members, 
                                        filteredAttendance, 
                                        filteredOvertime, 
                                        filteredTimeoff, 
                                        team.name,
                                        shifts,
                                        null
                                    )
                                    ModalManager.close()
                                }
                        }
                        .catch { error ->
                            console.error("❌ Failed to fetch time off data:", error)
                            window.alert("Failed to fetch time off data. Check console for details.")
                        }
                }
                .catch { error ->
                    console.error("❌ Failed to fetch overtime data:", error)
                    window.alert("Failed to fetch overtime data. Check console for details.")
                }
        }
        .catch { error ->
            console.error("❌ Failed to fetch attendance data:", error)
            window.alert("Failed to fetch attendance data. Check console for details.")
        }
}

/**
 * Generate CSV file from member list
 */
private fun generateMemberCSV(members: List<dynamic>, teamName: String) {
    console.log("📄 Generating CSV from ${members.size} members...")
    
    // CSV Header
    val headers = "Name,Email,Role,Join Date,Leave Quota"
    
    // CSV Data rows
    val rows = mutableListOf<String>()
    rows.add(headers)
    
    members.forEach { member ->
        val name = member.name as? String ?: "Unknown"
        val email = member.email as? String ?: "Unknown"
        val role = member.role as? String ?: "Unknown"
        val joinDate = member.joinDate as? String ?: "N/A"
        val quota = member.quota as? Int ?: 12
        
        console.log("Processing member: name=$name, email=$email, role=$role, joinDate=$joinDate, quota=$quota")
        
        // Escape CSV values (handle commas and quotes)
        val escapedName = escapeCSVValue(name)
        val escapedEmail = escapeCSVValue(email)
        val escapedRole = escapeCSVValue(role)
        
        val row = "$escapedName,$escapedEmail,$escapedRole,$joinDate,$quota"
        rows.add(row)
    }
    
    val csvContent = rows.joinToString("\n")
    console.log("CSV content length: ${csvContent.length}")
    console.log("CSV content:\n$csvContent")
    
    // Create blob and download
    val blob = js("new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })")
    val url = js("URL.createObjectURL(blob)") as String
    
    val link = document.createElement("a") as org.w3c.dom.HTMLAnchorElement
    link.href = url
    
    // Generate filename with team name and date
    val now = kotlin.js.Date()
    val year = now.getFullYear().toInt()
    val month = (now.getMonth() + 1).toString().padStart(2, '0')
    val day = now.getDate().toString().padStart(2, '0')
    val dateStr = "$year-$month-$day"
    
    val sanitizedTeamName = teamName.replace(Regex("[^a-zA-Z0-9-_]"), "_")
    link.download = "members_export_${sanitizedTeamName}_$dateStr.csv"
    
    link.style.display = "none"
    document.body?.appendChild(link)
    link.click()
    document.body?.removeChild(link)
    
    js("URL.revokeObjectURL(url)")
    
    console.log("✅ CSV export completed: ${link.download}")
    window.alert("✅ Members exported successfully!\n\nFile: ${link.download}\n\nTotal members: ${members.size}")
}

/**
 * Escape CSV values for safe export
 */
private fun escapeCSVValue(value: String): String {
    return if (value.contains(",") || value.contains("\"") || value.contains("\n")) {
        "\"${value.replace("\"", "\"\"")}\""
    } else {
        value
    }
}

/**
 * Show modal to select month and year for CSV export
 */
fun showCSVExportDatePicker(members: List<dynamic>, teamName: String) {
    val now = kotlin.js.Date()
    val currentMonth = (now.getMonth() + 1).toInt()
    val currentYear = now.getFullYear().toInt()
    
    console.log("📅 Showing CSV export date picker - Default: $currentMonth/$currentYear")
    
    // Get or create modal
    var modal = document.getElementById("csv-export-date-modal") as? org.w3c.dom.HTMLElement
    if (modal == null) {
        // Create the modal
        val modalDiv = document.createElement("div") as org.w3c.dom.HTMLDivElement
        modalDiv.id = "csv-export-date-modal"
        modalDiv.className = "modal-content fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-dark-800 border border-dark-700 rounded-2xl p-6 w-full max-w-md z-[101] shadow-2xl"
        modalDiv.innerHTML = """
            <div class="flex justify-between items-center mb-6">
                <h3 class="text-xl font-bold">Select Month & Year</h3>
                <button class="text-slate-400 hover:text-white text-2xl" onclick="document.getElementById('csv-export-date-modal').style.display='none';">×</button>
            </div>
            
            <div class="space-y-4 mb-6">
                <div>
                    <label class="block text-sm font-medium text-slate-300 mb-2">Month</label>
                    <select id="csv-export-month" class="w-full bg-dark-900 border border-dark-700 rounded-xl p-3 text-white focus:outline-none focus:border-brand-cyan">
                        <option value="1">January</option>
                        <option value="2">February</option>
                        <option value="3">March</option>
                        <option value="4">April</option>
                        <option value="5">May</option>
                        <option value="6">June</option>
                        <option value="7">July</option>
                        <option value="8">August</option>
                        <option value="9">September</option>
                        <option value="10">October</option>
                        <option value="11">November</option>
                        <option value="12">December</option>
                    </select>
                </div>
                
                <div>
                    <label class="block text-sm font-medium text-slate-300 mb-2">Year</label>
                    <select id="csv-export-year" class="w-full bg-dark-900 border border-dark-700 rounded-xl p-3 text-white focus:outline-none focus:border-brand-cyan">
                    </select>
                </div>
            </div>
            
            <div class="flex justify-end gap-3">
                <button class="px-4 py-2 rounded-xl text-slate-300 hover:bg-dark-700 transition" onclick="document.getElementById('csv-export-date-modal').style.display='none';">
                    Cancel
                </button>
                <button class="px-4 py-2 rounded-xl bg-brand-cyan text-white hover:bg-brand-green transition" id="csv-export-confirm-btn">
                    Export CSV
                </button>
            </div>
        """
        document.body?.appendChild(modalDiv)
        modal = modalDiv
    }
    
    // Populate year options (current year and 4 years back)
    val yearSelect = document.getElementById("csv-export-year") as? org.w3c.dom.HTMLSelectElement
    if (yearSelect != null && yearSelect.options.length == 0) {
        for (y in currentYear downTo (currentYear - 4)) {
            val option = document.createElement("option") as org.w3c.dom.HTMLOptionElement
            option.value = y.toString()
            option.text = y.toString()
            yearSelect.appendChild(option)
        }
    }
    
    // Set default values
    (document.getElementById("csv-export-month") as? org.w3c.dom.HTMLSelectElement)?.value = currentMonth.toString()
    (document.getElementById("csv-export-year") as? org.w3c.dom.HTMLSelectElement)?.value = currentYear.toString()
    
    // Set up confirm button
    val confirmBtn = document.getElementById("csv-export-confirm-btn") as? org.w3c.dom.HTMLButtonElement
    if (confirmBtn != null) {
        // Set onclick handler using JavaScript
        confirmBtn.asDynamic().onclick = {
            val selectedMonth = (document.getElementById("csv-export-month") as? org.w3c.dom.HTMLSelectElement)?.value?.toIntOrNull() ?: currentMonth
            val selectedYear = (document.getElementById("csv-export-year") as? org.w3c.dom.HTMLSelectElement)?.value?.toIntOrNull() ?: currentYear
            
            console.log("✅ Selected: $selectedMonth/$selectedYear")
            modal?.style?.display = "none"
            generateMemberCSV(members, teamName, selectedMonth, selectedYear)
            ModalManager.close()
        }
    }
    
    // Show modal
    modal.style.display = "block"
}

/**
 * Generate CSV file from member list with selected month/year
 * Includes shift templates, calendar, and member data
 */
private fun generateMemberCSV(members: List<dynamic>, teamName: String, month: Int = 0, year: Int = 0) {
    console.log("📄 Generating comprehensive CSV from ${members.size} members...")
    
    val now = kotlin.js.Date()
    val selectedMonth = if (month > 0) month else (now.getMonth() + 1).toInt()
    val selectedYear = if (year > 0) year else now.getFullYear().toInt()
    
    console.log("📅 CSV Export for: $selectedMonth/$selectedYear")
    
    // Build comprehensive CSV with sections
    val rows = mutableListOf<String>()
    
    // ===== SHIFT TEMPLATES SECTION =====
    rows.add("")
    rows.add("=== SHIFT TEMPLATES ===")
    rows.add("Code,Name,Time")
    rows.add("MOR,Morning,06:00-14:00")
    rows.add("AFT,Afternoon,14:00-22:00")
    rows.add("NIG,Night,22:00-06:00")
    
    // ===== CALENDAR SECTION (Member schedule) =====
    rows.add("")
    rows.add("=== CALENDAR (Member Schedule) ===")
    
    // Build calendar header with days
    val calendarHeader = mutableListOf("Name", "Email", "Role")
    for (day in 1..31) {
        calendarHeader.add(day.toString())
    }
    rows.add(calendarHeader.joinToString(","))
    
    // Add member schedule rows
    members.forEach { member ->
        val name = member.name as? String ?: "Unknown"
        val email = member.email as? String ?: "Unknown"
        val role = member.role as? String ?: "Unknown"
        
        val calendarRow = mutableListOf(escapeCSVValue(name), escapeCSVValue(email), escapeCSVValue(role))
        
        // Add days (placeholder data - in future could fetch actual schedule)
        for (day in 1..31) {
            calendarRow.add("-")
        }
        
        rows.add(calendarRow.joinToString(","))
    }
    
    // ===== MEMBER DATA SECTION =====
    rows.add("")
    rows.add("=== MEMBER DATA ===")
    rows.add("Name,Email,Role,Join Date,Leave Quota")
    
    members.forEach { member ->
        val name = member.name as? String ?: "Unknown"
        val email = member.email as? String ?: "Unknown"
        val role = member.role as? String ?: "Unknown"
        val joinDate = member.joinDate as? String ?: "N/A"
        val quota = member.quota as? Int ?: 12
        
        console.log("Processing member: name=$name, email=$email, role=$role, joinDate=$joinDate, quota=$quota")
        
        val escapedName = escapeCSVValue(name)
        val escapedEmail = escapeCSVValue(email)
        val escapedRole = escapeCSVValue(role)
        
        val row = "$escapedName,$escapedEmail,$escapedRole,$joinDate,$quota"
        rows.add(row)
    }
    
    // ===== NOTES SECTION =====
    rows.add("")
    rows.add("=== NOTES ===")
    rows.add("Generated for: $selectedMonth/$selectedYear")
    rows.add("Total Members: ${members.size}")
    rows.add("Export Date: ${kotlin.js.Date()}")
    rows.add("Team: ${escapeCSVValue(teamName)}")
    
    val csvContent = rows.joinToString("\n")
    console.log("CSV content length: ${csvContent.length}")
    console.log("CSV Sections: Shift Templates, Calendar, Member Data, Notes")
    console.log("CSV content:\n$csvContent")
    
    // Create blob and download
    val blob = js("new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })")
    val url = js("URL.createObjectURL(blob)") as String
    
    val link = document.createElement("a") as org.w3c.dom.HTMLAnchorElement
    link.href = url
    
    // Generate filename with team name, month, and year
    val monthStr = selectedMonth.toString().padStart(2, '0')
    val dateStr = "$selectedYear-$monthStr"
    
    val sanitizedTeamName = teamName.replace(Regex("[^a-zA-Z0-9-_]"), "_")
    link.download = "members_export_${sanitizedTeamName}_$dateStr.csv"
    
    link.style.display = "none"
    document.body?.appendChild(link)
    link.click()
    document.body?.removeChild(link)
    
    js("URL.revokeObjectURL(url)")
    
    console.log("✅ CSV export completed: ${link.download}")
    window.alert("✅ Members exported successfully!\n\nFile: ${link.download}\n\nMonth: $selectedMonth/$selectedYear\nTotal members: ${members.size}\n\nIncluded sections:\n- Shift Templates\n- Calendar (Member Schedule)\n- Member Data\n- Notes")
}

// FINAL EXCEL EXPORT - 100% Matching test_excel_simple.html
private fun generateMemberExcel(
    members: List<features.members.domain.Member>,
    attendanceRecords: List<AttendanceRecord>,
    overtimeRequests: List<OvertimeRequest>,
    timeoffRequests: List<TimeOffRequest>,
    teamName: String,
    shifts: List<features.shifts.domain.ShiftTemplate>,
    assignments: MutableMap<String, MutableMap<Int, String>>? = null
) {
    console.log("📊 Generating Excel matching test_excel_simple.html 100%")
    console.log("Member count: ${members.size}")
    if (members.isNotEmpty()) {
        console.log("First member sample: ${members[0].name}, ${members[0].email}, ${members[0].role}")
    }
    console.log("📊 Data counts - Attendance: ${attendanceRecords.size}, Overtime: ${overtimeRequests.size}, TimeOff: ${timeoffRequests.size}")
    
    val ExcelJS = js("window.ExcelJS")
    val workbook = js("new ExcelJS.Workbook()")
    val now = kotlin.js.Date()
    val monthName = arrayOf("January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December")[now.getMonth().toInt()]
    val year = now.getFullYear().toInt()
    val monthDays = arrayOf(31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31)[now.getMonth().toInt()]
    val worksheetName = "$monthName-$year"
    val ws = workbook.addWorksheet(worksheetName)
    
    // EXACT Colors from test_excel_simple.html
    val COLORS = js("""({
        MOR: "FFD700", AFT: "FF8C00", NIG: "4169E1",
        HEADER: "2C3E50", TEXT_W: "FFFFFF", TEXT_B: "000000",
        EMPTY: "F8F9FA", TIMEOFF: "E74C3C", OVERTIME: "F39C12", ATTENDANCE: "27AE60"
    })""")
    
    fun styleCell(cell: dynamic, bg: dynamic, text: dynamic, bold: Boolean, size: Int = 10) {
        // Use raw JS to ensure object structure matches exactly what ExcelJS expects
        js("""
            if (bg) {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } };
            }
            cell.font = { color: { argb: text }, bold: bold, size: size };
            cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        """)
    }

    var row = 1

    // ===== SHIFT TEMPLATES (Column L=12) =====
    ws.mergeCells(row, 12, row, 14)  // L-N merge
    var cell = ws.getCell(row, 12)
    styleCell(cell, COLORS.HEADER, COLORS.TEXT_W, true, 11)
    cell.value = "SHIFT TEMPLATES"
    row++

    // Headers
    styleCell(ws.getCell(row, 12), COLORS.HEADER, COLORS.TEXT_W, true, 9)
    ws.getCell(row, 12).value = "Code"
    styleCell(ws.getCell(row, 13), COLORS.HEADER, COLORS.TEXT_W, true, 9)
    ws.getCell(row, 13).value = "Name"
    styleCell(ws.getCell(row, 14), COLORS.HEADER, COLORS.TEXT_W, true, 9)
    ws.getCell(row, 14).value = "Time"
    row++

    // Shifts data - FROM DATABASE
    console.log("📋 Rendering ${shifts.size} shift templates from database")
    for (shift in shifts) {
        // Convert hex color to ARGB (add FF prefix for full opacity)
        val hexColor = shift.color.removePrefix("#")
        val argbColor = "FF$hexColor"
        
        styleCell(ws.getCell(row, 12), argbColor, COLORS.TEXT_B, true, 9)
        ws.getCell(row, 12).value = shift.code
        styleCell(ws.getCell(row, 13), argbColor, COLORS.TEXT_B, false, 9)
        ws.getCell(row, 13).value = shift.code
        styleCell(ws.getCell(row, 14), argbColor, COLORS.TEXT_B, false, 9)
        ws.getCell(row, 14).value = "${shift.startTime}-${shift.endTime}"
        row++
    }

    val templateEnd = row

    // ===== CALENDAR (Column O=15) =====
    var calRow = 1
    styleCell(ws.getCell(calRow, 15), COLORS.HEADER, COLORS.TEXT_W, true)
    ws.getCell(calRow, 15).value = "Name"
    styleCell(ws.getCell(calRow, 16), COLORS.HEADER, COLORS.TEXT_W, true)
    ws.getCell(calRow, 16).value = "Email"
    styleCell(ws.getCell(calRow, 17), COLORS.HEADER, COLORS.TEXT_W, true)
    ws.getCell(calRow, 17).value = "Role"

    for (day in 1..monthDays) {
        val col = 17 + day
        styleCell(ws.getCell(calRow, col), COLORS.HEADER, COLORS.TEXT_W, true, 9)
        ws.getCell(calRow, col).value = day
    }
    calRow++

    // Member rows - Calendar data
    for (member in members) {
        ws.getCell(calRow, 15).value = member.name
        ws.getCell(calRow, 16).value = member.email
        ws.getCell(calRow, 17).value = member.role

        val memberId = member.id
        val memberAssignments = if (assignments != null) assignments[memberId] else null

        for (day in 1..monthDays) {
            val col = 17 + day
            
            // Check real assignment
            var shiftCode: String? = null
            if (memberAssignments != null) {
                // Access map in JS way or Kotlin way depending on object structure
                // Assuming standard Kotlin map behavior if passed correctly
                shiftCode = memberAssignments[day]
            }

            if (shiftCode != null) {
                // Find shift template and use its color
                val shiftTemplate = shifts.find { it.code == shiftCode }
                val color = if (shiftTemplate != null) {
                    val hexColor = shiftTemplate.color.removePrefix("#")
                    "FF$hexColor"
                } else {
                    COLORS.EMPTY // Default if shift not found
                }
                
                styleCell(ws.getCell(calRow, col), color as String, COLORS.TEXT_B, true, 9)
                ws.getCell(calRow, col).value = shiftCode
            } else {
                ws.getCell(calRow, col).value = "-"
                val fontObj = js("({})")
                val colorObj = js("({})")
                colorObj.argb = "CCCCCC"
                fontObj.color = colorObj
                ws.getCell(calRow, col).font = fontObj
            }
        }
        calRow++
    }

    // ===== EMPTY SPACE (A-I) =====
    val endRow = kotlin.math.max(templateEnd, calRow)
    ws.mergeCells(1, 1, endRow, 9)
    styleCell(ws.getCell(1, 1), COLORS.EMPTY, COLORS.TEXT_B, false)
    ws.getCell(1, 1).value = ""

    // ===== MEMBER DATA SECTION =====
    val mdRow = endRow + 2
    var row2 = mdRow

    ws.mergeCells(row2, 1, row2, 9)
    styleCell(ws.getCell(row2, 1), COLORS.HEADER, COLORS.TEXT_W, true, 12)
    ws.getCell(row2, 1).value = "MEMBER DATA"
    row2++

    // Headers
    // Headers
    styleCell(ws.getCell(row2, 1), COLORS.HEADER, COLORS.TEXT_W, true)
    ws.getCell(row2, 1).value = "Name"
    styleCell(ws.getCell(row2, 2), COLORS.HEADER, COLORS.TEXT_W, true)
    ws.getCell(row2, 2).value = "Email"
    styleCell(ws.getCell(row2, 3), COLORS.HEADER, COLORS.TEXT_W, true)
    ws.getCell(row2, 3).value = "Role"
    row2++

    // Member data section - EXACT same logic as test
    for (member in members) {
        val mdStartRow = row2

        // Member info - first row for this member
        ws.getCell(row2, 1).value = member.name
        ws.getCell(row2, 2).value = member.email
        ws.getCell(row2, 3).value = member.role

        // Get data for this member
        val memberTimeoff = timeoffRequests.filter { it.userId == member.id }
        val memberOvertime = overtimeRequests.filter { it.userId == member.id }
        val memberAttendance = attendanceRecords.filter { it.userId == member.id }

        // TIME OFF section
        if (memberTimeoff.isNotEmpty()) {
            ws.mergeCells(row2, 4, row2, 9)
            styleCell(ws.getCell(row2, 4), COLORS.TIMEOFF, COLORS.TEXT_W, true)
            ws.getCell(row2, 4).value = "✈️ TIME OFF"
            row2++

            // Headers
            arrayOf("Date", "Period", "Days", "Status", "Category").forEachIndexed { i, h ->
                styleCell(ws.getCell(row2, 4 + i), COLORS.TIMEOFF, COLORS.TEXT_W, true, 9)
                ws.getCell(row2, 4 + i).value = h
            }
            row2++

            // Data rows - NO styling
            for (to in memberTimeoff) {
                ws.getCell(row2, 4).value = to.startDate
                ws.getCell(row2, 5).value = "${to.startDate} to ${to.endDate}"
                ws.getCell(row2, 6).value = "${to.days} days"
                ws.getCell(row2, 7).value = to.status
                ws.getCell(row2, 8).value = to.type
                row2++
            }
        }

        // OVERTIME section
        if (memberOvertime.isNotEmpty()) {
            ws.mergeCells(row2, 4, row2, 9)
            styleCell(ws.getCell(row2, 4), COLORS.OVERTIME, COLORS.TEXT_W, true)
            ws.getCell(row2, 4).value = "⏱️ OVERTIME"
            row2++

            // Headers
            arrayOf("Date", "Start", "End", "Hours", "Status").forEachIndexed { i, h ->
                styleCell(ws.getCell(row2, 4 + i), COLORS.OVERTIME, COLORS.TEXT_W, true, 9)
                ws.getCell(row2, 4 + i).value = h
            }
            row2++

            // Data rows - NO styling
            for (ot in memberOvertime) {
                ws.getCell(row2, 4).value = ot.date
                ws.getCell(row2, 5).value = ot.startTime
                ws.getCell(row2, 6).value = ot.endTime
                ws.getCell(row2, 7).value = "${ot.hours}h"
                ws.getCell(row2, 8).value = ot.status
                row2++
            }
        }

        // ATTENDANCE section
        if (memberAttendance.isNotEmpty()) {
            ws.mergeCells(row2, 4, row2, 9)
            styleCell(ws.getCell(row2, 4), COLORS.ATTENDANCE, COLORS.TEXT_W, true)
            ws.getCell(row2, 4).value = "📅 ATTENDANCE"
            row2++

            // Headers
            arrayOf("Date", "In", "Out", "Hours", "Status").forEachIndexed { i, h ->
                styleCell(ws.getCell(row2, 4 + i), COLORS.ATTENDANCE, COLORS.TEXT_W, true, 9)
                ws.getCell(row2, 4 + i).value = h
            }
            row2++

            // Data rows - NO styling
            for (att in memberAttendance) {
                ws.getCell(row2, 4).value = att.date
                ws.getCell(row2, 5).value = att.checkIn
                ws.getCell(row2, 6).value = att.checkOut
                ws.getCell(row2, 7).value = "8.5h" // Default hours
                ws.getCell(row2, 8).value = att.status
                row2++
            }
        }

        val mdEndRow = row2 - 1
        
        // Only merge if we actually have multiple rows
        if (mdEndRow > mdStartRow) {
            ws.mergeCells(mdStartRow, 1, mdEndRow, 1)
            ws.mergeCells(mdStartRow, 2, mdEndRow, 2)
            ws.mergeCells(mdStartRow, 3, mdEndRow, 3)
        }

        val align = js("({horizontal:'center',vertical:'middle',wrapText:true})")
        ws.getCell(mdStartRow, 1).alignment = align
        ws.getCell(mdStartRow, 2).alignment = align
        ws.getCell(mdStartRow, 3).alignment = align

        // Empty row between members
        row2++
    }

    // ===== VERTICAL SEPARATOR (Column J=10) =====
    for (r in 1..row2) {
        val borderObj = js("({})")
        borderObj.right = js("({style:'medium',color:'000000'})")
        ws.getCell(r, 10).border = borderObj
    }

    // ===== COLUMN WIDTHS - EXACT from test =====
    ws.getColumn(1).width = 18
    ws.getColumn(2).width = 25
    ws.getColumn(3).width = 12
    ws.getColumn(4).width = 15
    ws.getColumn(5).width = 15
    ws.getColumn(6).width = 20
    ws.getColumn(7).width = 12
    ws.getColumn(8).width = 12
    ws.getColumn(9).width = 20
    ws.getColumn(10).width = 2
    ws.getColumn(11).width = 2
    ws.getColumn(12).width = 8
    ws.getColumn(13).width = 12
    ws.getColumn(14).width = 15
    ws.getColumn(15).width = 18
    ws.getColumn(16).width = 25
    ws.getColumn(17).width = 12

    for (i in 1..monthDays) {
        ws.getColumn(17 + i).width = 4
    }

    // ===== SAVE =====
    val timestamp = js("new Date().toISOString().slice(0,10)") as String
    val filename = "${teamName}_${worksheetName}_$timestamp.xlsx"

    workbook.xlsx.writeBuffer().then { buffer: dynamic ->
        val blob = js("new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })")
        val url = js("URL.createObjectURL(blob)")
        val link = js("document.createElement('a')")
        link.href = url
        link.download = filename
        link.click()
        js("URL.revokeObjectURL(url)")

        console.log("✅ Excel export complete!")
        console.log("📁 File: $filename")

        window.alert(
            "✅ Export successful!\\n\\n" +
            "File: $filename\\n" +
            "Members: ${members.size}\\n" +
            "Period: $worksheetName\\n\\n" +
            "Included:\\n" +
            "- Shift Templates\\n" +
            "- Calendar with Member Schedule\\n" +
            "- Member Data (Time Off, Overtime, Attendance)"
        )
    }
}
